import { businesses, supabaseAdmin } from '@/integrations/supabase/client';
import { supabase } from '@/integrations/supabase/client';
import { StorageService } from './storage/StorageService';
import { toast } from 'sonner';
import { uploadFile, getStorageUrl } from '@/utils/storage';

// Types
export interface PitchDeckData {
  company_name: string;
  tagline: string | null;
  description: string | null;
  logo_url: string | null;
  pitch_deck_url: string | null;
  development_stage: 'development' | 'operation';
  industry_type: string | null;
  website: string | null;
  contact_email: string | null;
  problem_statement: string | null;
  solution_description: string | null;
  market_size: string | null;
  funding_goal: number | null;
  investment_highlights: string[] | null;
  competitive_edge: string[] | null;
  target_segment: string | null;
  growth_drivers: string | null;
  team_members: any | null;
  business_model: any | null;
  traction: any | null;
  product_offerings: any | null;
  financial_projections: any | null;
  entity_type: string;
  status: string;
}

// For database type (includes IDs and timestamps)
export interface DatabaseCompanyProfile extends PitchDeckData {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Service for handling company profile and pitch deck data
 */
export default class CompanyProfileService {
  private static readonly COMPANY_ASSETS_BUCKET = 'company-assets';

  /**
   * Upload a file to storage
   */
  private static async uploadFile(file: File, path: string): Promise<string> {
    const result = await uploadFile(file, path, this.COMPANY_ASSETS_BUCKET);
    if (!result) {
      throw new Error('Failed to upload file');
    }
    return result.url;
  }
  
  /**
   * Get all company profiles
   */
  static async getAllCompanyProfiles(): Promise<DatabaseCompanyProfile[]> {
    try {
      const { data, error } = await businesses(true)
        .select('*');
      
      if (error) {
        throw error;
      }
      
      return data as unknown as DatabaseCompanyProfile[];
    } catch (error) {
      console.error('Error fetching company profiles:', error);
      throw error;
    }
  }
  
  /**
   * Get all active company profiles
   */
  static async getActiveCompanyProfiles(): Promise<DatabaseCompanyProfile[]> {
    try {
      const { data, error } = await businesses(true)
        .select('*')
        .eq('status', 'active');
      
      if (error) {
        throw error;
      }
      
      return data as unknown as DatabaseCompanyProfile[];
    } catch (error) {
      console.error('Error fetching active company profiles:', error);
      throw error;
    }
  }
  
  /**
   * Get a company profile by ID
   */
  static async getCompanyProfile(id: string): Promise<DatabaseCompanyProfile> {
    try {
      const { data, error } = await businesses()
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        throw error;
      }
      
      return data as unknown as DatabaseCompanyProfile;
    } catch (error) {
      console.error(`Error fetching company profile ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Get company profiles by entity type
   */
  static async getCompanyProfilesByEntityType(entityType: string): Promise<DatabaseCompanyProfile[]> {
    try {
      const { data, error } = await businesses()
        .select('*')
        .eq('entity_type', entityType)
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data as unknown as DatabaseCompanyProfile[];
    } catch (error) {
      console.error(`Error fetching company profiles by entity type ${entityType}:`, error);
      throw error;
    }
  }
  
  /**
   * Create a new company profile
   */
  static async createCompanyProfile(profileData: PitchDeckData, ownerId: string): Promise<DatabaseCompanyProfile> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('No authenticated user');

      // Get user's role from profiles table
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.id)
        .single();
      
      if (profileError) throw profileError;
      
      console.log('Creating company profile with user:', user.id);

      const businessData = {
        ...profileData,
        entity_type: profileData.entity_type || 'business',
        // Only allow admin to set status to active, otherwise force draft
        status: userProfile?.user_type === 'admin' ? (profileData.status || 'active') : 'draft',
        user_id: user.id,
        // Ensure required fields have values
        description: profileData.description || '',
        development_stage: profileData.development_stage || 'development',
        industry_type: profileData.industry_type || 'Other'
      };

      const { data, error } = await supabase
        .from('businesses')
        .insert(businessData)
        .select()
        .single();
      
      if (error) {
        console.error('Database error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }
      
      return data as unknown as DatabaseCompanyProfile;
    } catch (error) {
      console.error('Error creating company profile:', error);
      throw error;
    }
  }
  
  /**
   * Update an existing company profile
   */
  static async updateCompanyProfile(id: string, profileData: Partial<PitchDeckData>): Promise<DatabaseCompanyProfile> {
    try {
      // Ensure JSON fields are properly stringified
      const processedData = {
        ...profileData,
        business_model: profileData.business_model ? JSON.stringify(profileData.business_model) : null,
        traction: profileData.traction ? JSON.stringify(profileData.traction) : null,
        team_members: profileData.team_members ? JSON.stringify(profileData.team_members) : null,
        product_offerings: profileData.product_offerings ? JSON.stringify(profileData.product_offerings) : null,
        financial_projections: profileData.financial_projections ? JSON.stringify(profileData.financial_projections) : null,
        updated_at: new Date().toISOString()
      };

      // Use supabaseAdmin to bypass RLS
      const { data, error } = await supabaseAdmin
        .from('businesses')
        .update(processedData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      
      // Parse JSON fields back for the return value
      return {
        ...data,
        business_model: data.business_model ? JSON.parse(data.business_model) : null,
        traction: data.traction ? JSON.parse(data.traction) : null,
        team_members: data.team_members ? JSON.parse(data.team_members) : null,
        product_offerings: data.product_offerings ? JSON.parse(data.product_offerings) : null,
        financial_projections: data.financial_projections ? JSON.parse(data.financial_projections) : null,
      } as DatabaseCompanyProfile;
    } catch (error) {
      console.error(`Error updating company profile ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete a company profile
   */
  static async deleteCompanyProfile(id: string): Promise<void> {
    try {
      const { error } = await businesses(true)
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error(`Error deleting company profile ${id}:`, error);
      throw error;
    }
  }

  /**
   * Upload a company logo
   */
  static async uploadCompanyLogo(file: File, companyName: string): Promise<string> {
    try {
      console.log('CompanyProfileService.uploadCompanyLogo - Starting upload:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        companyName
      });

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB.');
      }

      // Clean company name for file path
      const cleanCompanyName = companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `company-logos/${cleanCompanyName}-${Date.now()}.${file.name.split('.').pop()}`;

      // Upload file directly using the upload utility
      const result = await uploadFile(file, path, this.COMPANY_ASSETS_BUCKET);
      if (!result) {
        throw new Error('Failed to get URL for uploaded logo');
      }

      console.log('CompanyProfileService.uploadCompanyLogo - Upload successful:', result.url);
      return result.url;
    } catch (error) {
      console.error('CompanyProfileService.uploadCompanyLogo - Error:', error);
      throw error instanceof Error 
        ? error 
        : new Error('An unexpected error occurred while uploading the company logo');
    }
  }

  /**
   * Upload a pitch deck
   */
  static async uploadPitchDeck(file: File, companyName: string): Promise<string> {
    try {
      console.log('CompanyProfileService.uploadPitchDeck - Starting upload:', {
        fileName: file.name,
        companyName
      });

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload a PDF or PowerPoint file.');
      }

      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        throw new Error('File size must be less than 50MB.');
      }

      // Clean company name for file path
      const cleanCompanyName = companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `pitch-decks/${cleanCompanyName}-${Date.now()}.${file.name.split('.').pop()}`;

      // Upload file using uploadFile utility
      const result = await uploadFile(file, path, this.COMPANY_ASSETS_BUCKET);
      if (!result) {
        throw new Error('Failed to upload pitch deck');
      }

      console.log('CompanyProfileService.uploadPitchDeck - Upload successful:', result.url);
      return result.url;
    } catch (error) {
      console.error('CompanyProfileService.uploadPitchDeck - Error:', error);
      throw error instanceof Error 
        ? error 
        : new Error('An unexpected error occurred while uploading the pitch deck');
    }
  }

  /**
   * Get upload button label
   */
  static async getUploadButtonLabel(): Promise<string> {
    try {
      const settings = await supabase.from('admin_settings')
        .select('setting_value')
        .eq('setting_name', 'file_upload_button_label')
        .single();
      
      return settings.data?.setting_value || 'Upload File';
    } catch (error) {
      console.error('Error getting upload button label:', error);
      return 'Upload File';
    }
  }
}
